import SpinnerArea from "../../../../components/SpinnerArea";
import useService from "../../../../hooks/useService";

import tasksService from "../../../../services/tasks.service";
import { Button, FormControl, FormLabel, TextField } from "@mui/material";
import { Trans, useTranslation } from "react-i18next";
import { FormikProps, useFormik } from "formik";
import {
  ColleagueFeedbackTaskCommentDto,
  DOIAssigmentDto,
} from "../../../../models/task.model";
import * as Yup from "yup";
import styles from "../Task.module.scss";
import { useContext, useEffect, useState } from "react";
import MainContext from "../../../../context/mainContext";
import { useNavigate } from "react-router";
import { SaveSuccessDialog, CancelDialog } from "../TaskDialogs";

import { TaskTypeProps } from "./TaskTypeProps";
import moment from "moment";
import { validateBeforeExecute } from "../../../../misc/utils.functions";

const DOIAssigment: React.FC<TaskTypeProps> = ({ taskHeader }) => {
  const { t } = useTranslation();
  const { service, currentUser } = useContext(MainContext);
  const navigate = useNavigate();

  const [details, loadingDetails] = useService(() =>
    tasksService.doiAssignationDetails(taskHeader.id)
  );

  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saving, setSaving] = useState(false);
  const [cancelDialogShow, cancelDialogShowSet] = useState(false);

  const [assignTo, setAssignTo] = useState<Number | null>(null);

  useEffect(() => {
    if (details?.assignedToId != null) {
      setAssignTo(details?.assignedToId);
    }
  }, [details]);

  const validationSchema = Yup.object().shape({
    doiNumber: Yup.string().nullable().required(t("requiredField")!).max(255),
  });

  const formik = useFormik<DOIAssigmentDto>({
    initialValues: {
      doiNumber: details?.doiNumber ?? null,
      taskId: taskHeader.id,
    },
    onSubmit: () => console.log("submit"),
    validationSchema: validationSchema,
    validateOnChange: true,
    validateOnMount: true,
    enableReinitialize: true,
  });

  const sendDOIAssignation = (values: DOIAssigmentDto) => {
    service(() => tasksService.doiAssignation({ ...values }), {
      callback: () => {
        setSaveSuccess(true);
      },
      loader: setSaving,
    });
  };

  const assign = (values: DOIAssigmentDto) => {
    service(() => tasksService.doiAssignationAssignToMe(values.taskId), {
      callback: () => {
        setAssignTo(currentUser?.id ?? null);
      },
      loader: setSaving,
    });
  };

  const cancel = (formik: FormikProps<DOIAssigmentDto>) => {
    if (formik.dirty) cancelDialogShowSet(true);
    else navigate(-1);
  };

  return (
    <SpinnerArea loading={saving || loadingDetails}>
      <form onSubmit={formik.handleSubmit} onReset={formik.handleReset}>
        <div className={styles.doiAssigment}>
          <Button
            className={styles.assignToMe}
            disabled={assignTo != null && assignTo == currentUser?.id}
            onClick={() => assign(formik.values)}
          >
            {t("assignToMe")}
          </Button>
          <div className={styles.assignedTo}>
            {assignTo != null && assignTo != currentUser?.id ? (
              <p>
                {t("knowledgeTaskAssignedTo", {
                  userName: details?.assignedTo,
                })}
              </p>
            ) : (
              <></>
            )}
          </div>
          <p>
            <Trans 
              i18nKey="knowledgeTaskLegend"
              components={{
                bold: <strong />,
              }}
              values={{
                deadline: taskHeader.deadline
                ? moment(taskHeader.deadline).format("MM/DD/yyyy")
                : "[deadline]",
              }}
            />

          </p>
          <div className={styles.instructionsSection}>
            <p>{t("knowledgeTaskDOINumberMessage")}</p>
          </div>
          <div className={styles.doiNumberSection}>
            <FormControl fullWidth>
              <FormLabel id="doiNumber-label">{t("doiNumber")}</FormLabel>
              <TextField
                id="doiNumber"
                name="doiNumber"
                fullWidth
                disabled={
                  assignTo === null ||
                  assignTo !== currentUser?.id ||
                  details?.doiNumber != null
                }
                placeholder={t("doiNumber")!}
                value={formik.values.doiNumber || ""}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                helperText={formik.touched.doiNumber && formik.errors.doiNumber}
                error={
                  formik.touched.doiNumber && Boolean(formik.errors.doiNumber)
                }
              />
            </FormControl>
          </div>
        </div>

        <div className={styles.formActions}>
          <Button className={styles.cancel} onClick={() => cancel(formik)}>
            {t("cancel")}
          </Button>

          <Button
            className={styles.done}
            onClick={() => {
              return validateBeforeExecute(formik, () =>
                sendDOIAssignation(formik.values)
              );
            }}
          >
            {t("done")}
          </Button>
        </div>
      </form>
      <CancelDialog
        open={cancelDialogShow}
        onAccept={() => navigate(-1)}
        onClose={() => cancelDialogShowSet(false)}
      />
      <SaveSuccessDialog
        open={saveSuccess}
        onAccept={() => {}}
        onClose={() => setSaveSuccess(false)}
        title={t("saveSuccess")}
      />
    </SpinnerArea>
  );
};

export default DOIAssigment;
